/**
 * CrisisSelectorContent — Practitioner selector UI
 *
 * Extracted from CrisisPractitionerPrompt.tsx as part of E2 refactoring, S2.3 (#742).
 * Renders the list of practitioners with radio selection UI.
 */

import { View, Text, Pressable } from 'react-native';
import type { PractitionerCrisisConsent } from '@dramadetector/api-client';
import { styles, type TFunction } from '../styles/crisis-practitioner.styles';

export interface SelectorContentProps {
  t: TFunction;
  practitioners: PractitionerCrisisConsent[];
  selectedPractitioner: PractitionerCrisisConsent | null;
  onSelect: (practitioner: PractitionerCrisisConsent) => void;
  onSend: () => void;
  onCancel: () => void;
}

export function SelectorContent({
  t,
  practitioners,
  selectedPractitioner,
  onSelect,
  onSend,
  onCancel,
}: SelectorContentProps) {
  return (
    <View style={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          {t('practitioner_selector.title')}
        </Text>
        <Text style={styles.headerSubtitle}>
          {t('practitioner_selector.subtitle')}
        </Text>
      </View>

      {/* Practitioner List */}
      <View style={styles.practitionerList}>
        {practitioners.map((practitioner) => {
          const isSelected =
            selectedPractitioner?.practitioner_id ===
            practitioner.practitioner_id;
          return (
            <Pressable
              key={practitioner.id}
              onPress={() => onSelect(practitioner)}
              style={[
                styles.practitionerItem,
                isSelected && styles.practitionerItemSelected,
              ]}
              accessibilityRole="radio"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={t('practitioner_selector.select_accessibility')}
              testID={`practitioner-${practitioner.practitioner_id}`}
            >
              <View style={styles.practitionerInfo}>
                <View
                  style={[
                    styles.practitionerRadio,
                    isSelected && styles.practitionerRadioSelected,
                  ]}
                >
                  {isSelected && <View style={styles.practitionerRadioDot} />}
                </View>
                <View style={styles.practitionerDetails}>
                  {/* Note: In a real app, you'd fetch practitioner name/email */}
                  <Text style={styles.practitionerName}>
                    {t('practitioner_selector.default_name')}
                  </Text>
                  <Text style={styles.practitionerPreference}>
                    {practitioner.crisis_notification_contact_method === 'email'
                      ? '📧 Email'
                      : practitioner.crisis_notification_contact_method ===
                          'push'
                        ? '🔔 Push'
                        : '📧🔔 Email & Push'}
                  </Text>
                </View>
              </View>
            </Pressable>
          );
        })}
      </View>

      {/* Action Buttons */}
      <View style={styles.buttonContainer}>
        <Pressable
          onPress={onSend}
          style={[
            styles.primaryButton,
            !selectedPractitioner && styles.buttonDisabled,
          ]}
          disabled={!selectedPractitioner}
          accessibilityRole="button"
          testID="crisis-practitioner-send"
        >
          <Text style={styles.primaryButtonText}>
            {t('practitioner_selector.send_request')}
          </Text>
        </Pressable>

        <Pressable
          onPress={onCancel}
          style={styles.secondaryButton}
          accessibilityRole="button"
          testID="crisis-practitioner-cancel"
        >
          <Text style={styles.secondaryButtonText}>
            {t('practitioner_selector.cancel')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
